import { Box, Chip, ListItemIcon, ListItemText, MenuItem, Stack } from "@mui/material";
import { isNil, orderBy } from "lodash-es";
import { useMemo, useState } from "react";

import { TypeHelper } from "@/common/helpers/type";
import { getTypedPath } from "@/common/helpers/typedPath";
import { ApiEnumName } from "@/common/services/enum";
import { ViewVariant } from "@/common/ts/layout";
import {
  AppPermission,
  DamageDetectionCostEvaluationLinkDto,
  DamageDetectionCostEvaluationOverviewDto,
  DamageDetectionCostEvaluationStatus,
  DamageDetectionDto,
  EntityOriginType,
  EntitySourceType,
} from "@/core/api/generated/v1";

import NoDataAlert from "../../AppAlerts/NoDataAlert";
import AuthorizedElement from "../../Auth/AuthorizedElement";
import AppIconButton from "../../Button/AppIconButton";
import DataTabular from "../../DataTabular/DataTabular";
import Datetime from "../../Datetime/Datetime";
import InlineApiEnumValue from "../../Enum/InlineApiEnumValue";
import AppIcon from "../../Icons/AppIcon";
import MenuWithTrigger from "../../Menu/MenuWithTrigger";
import AppModal from "../../Modals/AppModal";
import AppModalContent from "../../Modals/AppModalContent";
import AppModalTitle from "../../Modals/AppModalTitle";
import AppPopover from "../../Popover/AppPopover";
import AppPopoverContent from "../../Popover/AppPopoverContent";
import TableCellContentOfCount from "../../Table/TableCell/TableCellContentOfCount";
import AppTypography from "../../Text/AppTypography";
import DamageCostEvaluationLink from "../DamageCostEvaluation/DamageCostEvaluationLink";
import DamageCostEvaluationPaginatedList from "../DamageCostEvaluation/ListView/DamageCostEvaluationPaginatedList";
import TenantRequestPaginatedList from "../TenantRequest/ListView/TenantRequestPaginatedList";
import TenantRequestLink from "../TenantRequest/TenantRequestLink";
import VehicleDamageLink from "../VehicleDamage/VehicleDamageLink";

interface Props {
  /**
   * normal - full info to display on page
   * compact - minimal info with popover
   * tableCell - counter with popover
   */
  variant?: "normal" | "compact" | "tableCell";
  damageDetection: DamageDetectionDto;
  overview: Nil<DamageDetectionCostEvaluationOverviewDto>;
  /** All if not set. */
  originType?: EntityOriginType;
}

export function DamageDetectionCostEvaluationOverviewDisplay({
  variant = "normal",
  damageDetection,
  overview,
  originType,
}: Props) {
  const isDisplayAll = isNil(originType);
  const isDisplayLocal = isDisplayAll || originType === EntityOriginType.Local;
  const isDisplayExternal = isDisplayAll || originType === EntityOriginType.External;

  const [isDamageCostEvaluationListModalOpen, setIsDamageCostEvaluationListModalOpen] =
    useState(false);
  const [isTenantRequestListModalOpen, setIsTenantRequestListModalOpen] = useState(false);

  const linksDesc = useMemo(
    () => (overview?.links ? orderBy(overview.links, (x) => x.createdAt, "desc") : undefined),
    [overview?.links],
  );
  const localLinks = useMemo(
    () => linksDesc?.filter((x) => x.originType === EntityOriginType.Local),
    [linksDesc],
  );
  const externalLinks = useMemo(
    () => linksDesc?.filter((x) => x.originType === EntityOriginType.External),
    [linksDesc],
  );

  const renderLinkList = (params: {
    variant: "normal" | "compact";
    originType: EntityOriginType;
    links: Nil<DamageDetectionCostEvaluationLinkDto[]>;
  }) => (
    <DataTabular
      isColumnSelectorEnabled={false}
      columns={[
        {
          field: getTypedPath<DamageDetectionCostEvaluationLinkDto>().damageCostEvaluationId.$path,
          if: params.originType === EntityOriginType.Local,
          title: "Cost evaluation",
          width: 350,
          isVisible: true,
          isHideable: true,
          isSortable: false,
          isFilterable: false,
          renderCell: (item) =>
            item.damageCostEvaluationId ? (
              <DamageCostEvaluationLink entityId={item.damageCostEvaluationId} entity={undefined} />
            ) : (
              "-"
            ),
        },
        {
          field: getTypedPath<DamageDetectionCostEvaluationLinkDto>().tenantRequestId.$path,
          if: params.originType === EntityOriginType.External,
          title: "C2C request",
          width: 350,
          isVisible: true,
          isHideable: true,
          isSortable: false,
          isFilterable: false,
          renderCell: (item) =>
            item.tenantRequestId ? (
              <TenantRequestLink entityId={item.tenantRequestId} entity={undefined} />
            ) : (
              "-"
            ),
        },
        {
          field: getTypedPath<DamageDetectionCostEvaluationLinkDto>().status.$path,
          title: "Status",
          width: 140,
          isVisible: true,
          isHideable: true,
          isSortable: false,
          isFilterable: false,
          renderCell: (item) => (
            <InlineApiEnumValue
              type={ApiEnumName.DamageDetectionCostEvaluationStatus}
              value={item.status || DamageDetectionCostEvaluationStatus.Unknown}
            />
          ),
        },
        {
          field: getTypedPath<DamageDetectionCostEvaluationLinkDto>().vehicleDamageIds.$path,
          title: "Damages",
          width: 100,
          isVisible: true,
          isHideable: true,
          isSortable: false,
          isFilterable: false,
          renderCell: (item) =>
            TypeHelper.isNotEmptyArray(item.vehicleDamageIds) ? (
              <TableCellContentOfCount
                count={item.vehicleDamageIds?.length ?? 0}
                size={variant === "normal" ? "medium" : "extraSmall"}
                popoverContent={
                  <Stack gap={0.5}>
                    {item.vehicleDamageIds?.map((vehicleDamageId, i) => (
                      <Stack key={vehicleDamageId || i} direction='row' gap={1} alignItems='center'>
                        <Box component='span'>{i + 1}.</Box>
                        <VehicleDamageLink
                          vehicleId={undefined}
                          entityId={vehicleDamageId}
                          entity={undefined}
                        />
                      </Stack>
                    ))}
                  </Stack>
                }
              />
            ) : (
              "-"
            ),
        },
        {
          field: getTypedPath<DamageDetectionCostEvaluationLinkDto>().createdAt.$path,
          if: params.variant === "normal",
          title: "Created at",
          width: 150,
          isVisible: true,
          isHideable: true,
          isSortable: false,
          isFilterable: false,
          renderCell: (item) =>
            item.createdAt ? (
              <Datetime datetime={item.createdAt} withDurationFromNow={false} />
            ) : (
              "-"
            ),
        },
        {
          field: getTypedPath<DamageDetectionCostEvaluationLinkDto>().updatedAt.$path,
          if: params.variant === "normal",
          title: "Updated at",
          width: 150,
          isVisible: true,
          isHideable: true,
          isSortable: false,
          isFilterable: false,
          renderCell: (item) =>
            item.updatedAt ? (
              <Datetime datetime={item.updatedAt} withDurationFromNow={false} />
            ) : (
              "-"
            ),
        },
      ]}
      rows={params.links}
      getRowId={(item) => item.id!}
      getRowHeight={variant !== "normal" ? () => "auto" : undefined} // make rows compact
    />
  );

  const mainContent = (
    <Box>
      {variant === "tableCell" && (
        <Box>
          <TableCellContentOfCount
            count={
              isDisplayAll
                ? (linksDesc?.length ?? 0)
                : isDisplayLocal
                  ? (localLinks?.length ?? 0)
                  : isDisplayExternal
                    ? (externalLinks?.length ?? 0)
                    : 0
            }
            popoverContent={undefined}
          />
        </Box>
      )}

      {variant === "compact" && (
        <Box sx={{ width: "fit-content" }}>
          {TypeHelper.isEmptyArray(overview?.links) && <Box>No cost evaluations yet</Box>}

          {TypeHelper.isNotEmptyArray(overview?.links) && (
            <Stack direction='row' gap={0.5} alignItems='center'>
              {isDisplayLocal && (
                <Chip
                  variant='outlined'
                  color='default'
                  size='small'
                  label={<>Local: {localLinks?.length ?? 0}</>}
                />
              )}

              {isDisplayExternal && (
                <Chip
                  variant='outlined'
                  color='default'
                  size='small'
                  label={<>External: {externalLinks?.length ?? 0}</>}
                />
              )}
            </Stack>
          )}
        </Box>
      )}

      {variant === "normal" && (
        <Box>
          {isDisplayLocal && (
            <Stack gap={1}>
              <Stack direction='row' gap={0.5} alignItems='center'>
                <AppTypography
                  sx={{ width: "fit-content" }}
                  component='span'
                  variant='h5'
                  decoration={{ variant: "helpText" }}
                  tooltipProps={{
                    title: "Damage cost evaluations created from this damage detection.",
                    placement: "bottom-start",
                  }}
                >
                  Local
                </AppTypography>

                {TypeHelper.isNotEmptyArray(localLinks) && (
                  <AuthorizedElement permissions={[AppPermission.DamageCostEvaluationRead]}>
                    <MenuWithTrigger
                      withAuthCloseOnClick
                      trigger={
                        <AppIconButton size='small'>
                          <AppIcon of='moreVert' />
                        </AppIconButton>
                      }
                    >
                      <MenuItem
                        onClick={async () => {
                          setIsDamageCostEvaluationListModalOpen(true);
                        }}
                      >
                        <ListItemIcon>
                          <AppIcon of='view' fontSize='small' />
                        </ListItemIcon>
                        <ListItemText>View details</ListItemText>
                      </MenuItem>
                    </MenuWithTrigger>
                  </AuthorizedElement>
                )}
              </Stack>

              {TypeHelper.isEmptyArray(localLinks) && (
                <NoDataAlert title='No cost evaluations yet.' description={undefined} />
              )}

              {TypeHelper.isNotEmptyArray(localLinks) && (
                <Box>
                  {renderLinkList({
                    variant: "normal",
                    originType: EntityOriginType.Local,
                    links: localLinks,
                  })}

                  <AuthorizedElement permissions={[AppPermission.DamageCostEvaluationRead]}>
                    <AppModal
                      maxWidth='xl'
                      open={isDamageCostEvaluationListModalOpen}
                      onClose={() => setIsDamageCostEvaluationListModalOpen(false)}
                    >
                      <AppModalTitle
                        title='Damage cost evaluations'
                        withClose
                        onClose={() => {
                          setIsDamageCostEvaluationListModalOpen(false);
                        }}
                      />

                      <AppModalContent>
                        <AuthorizedElement
                          permissions={[AppPermission.DamageCostEvaluationRead]}
                          withAlert
                        >
                          <DamageCostEvaluationPaginatedList
                            displayProps={{
                              viewVariant: ViewVariant.Tab,
                              breadcrumbs: false,
                              counters: false,
                              header: false,
                              create: false,
                            }}
                            defaultValues={{
                              limit: 10,
                              source: {
                                sourceId: {
                                  equal: damageDetection.id,
                                },
                                type: {
                                  equal: EntitySourceType.DamageDetection,
                                },
                              },
                            }}
                          />
                        </AuthorizedElement>
                      </AppModalContent>
                    </AppModal>
                  </AuthorizedElement>
                </Box>
              )}
            </Stack>
          )}

          {isDisplayExternal && (
            <Stack gap={1}>
              <Stack direction='row' gap={0.5} alignItems='center'>
                <AppTypography
                  sx={{ width: "fit-content" }}
                  component='span'
                  variant='h5'
                  decoration={{ variant: "helpText" }}
                  tooltipProps={{
                    title:
                      "C2C requests for damage cost evaluation created and sent from this damage detection.",
                    placement: "bottom-start",
                  }}
                >
                  External
                </AppTypography>

                {TypeHelper.isNotEmptyArray(externalLinks) && (
                  <AuthorizedElement permissions={[AppPermission.TenantRequestRead]}>
                    <MenuWithTrigger
                      withAuthCloseOnClick
                      trigger={
                        <AppIconButton size='small'>
                          <AppIcon of='moreVert' />
                        </AppIconButton>
                      }
                    >
                      <MenuItem
                        onClick={async () => {
                          setIsTenantRequestListModalOpen(true);
                        }}
                      >
                        <ListItemIcon>
                          <AppIcon of='view' fontSize='small' />
                        </ListItemIcon>
                        <ListItemText>View details</ListItemText>
                      </MenuItem>
                    </MenuWithTrigger>
                  </AuthorizedElement>
                )}
              </Stack>

              {TypeHelper.isEmptyArray(externalLinks) && (
                <NoDataAlert title='No cost evaluations yet.' description={undefined} />
              )}

              {TypeHelper.isNotEmptyArray(externalLinks) && (
                <Box>
                  {renderLinkList({
                    variant: "normal",
                    originType: EntityOriginType.External,
                    links: externalLinks,
                  })}

                  <AuthorizedElement permissions={[AppPermission.DamageCostEvaluationRead]}>
                    <AppModal
                      maxWidth='xl'
                      open={isTenantRequestListModalOpen}
                      onClose={() => setIsTenantRequestListModalOpen(false)}
                    >
                      <AppModalTitle
                        title='C2C requests for damage cost evaluation'
                        withClose
                        onClose={() => {
                          setIsTenantRequestListModalOpen(false);
                        }}
                      />

                      <AppModalContent>
                        <AuthorizedElement
                          permissions={[AppPermission.TenantRequestRead]}
                          withAlert
                        >
                          <TenantRequestPaginatedList
                            displayProps={{
                              viewVariant: ViewVariant.Tab,
                              breadcrumbs: false,
                              counters: false,
                              header: false,
                              create: false,
                            }}
                            defaultValues={{
                              limit: 10,
                              source: {
                                sourceId: {
                                  equal: damageDetection.id,
                                },
                                type: {
                                  equal: EntitySourceType.DamageDetection,
                                },
                              },
                            }}
                          />
                        </AuthorizedElement>
                      </AppModalContent>
                    </AppModal>
                  </AuthorizedElement>
                </Box>
              )}
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );

  return (
    <Box>
      <AppPopover
        enabled={variant !== "normal"}
        trigger={mainContent}
        hoverBehavior={{ closeBehavior: "onTriggerOrContentMouseLeave" }}
      >
        <AppPopoverContent
          sx={{
            minWidth: { xxs: "90vw", md: 500 },
            maxWidth: { xxs: "90vw", md: 700, lg: 1000 },
            width: { xxs: "90vw", md: 500, lg: 700 },
            maxHeight: { xxs: "80vh", md: 500, lg: 700 },
          }}
        >
          <Box>
            <Box>
              <Stack gap={2}>
                {isDisplayLocal && (
                  <Stack gap={0}>
                    <AppTypography variant='h6'>Local</AppTypography>

                    {TypeHelper.isEmptyArray(localLinks) && <Box>No cost evaluations yet.</Box>}

                    {TypeHelper.isNotEmptyArray(localLinks) &&
                      renderLinkList({
                        variant: "compact",
                        originType: EntityOriginType.Local,
                        links: localLinks,
                      })}
                  </Stack>
                )}

                {isDisplayExternal && (
                  <Stack gap={0}>
                    <AppTypography variant='h6'>External</AppTypography>

                    {TypeHelper.isEmptyArray(externalLinks) && <Box>No cost evaluations yet.</Box>}

                    {TypeHelper.isNotEmptyArray(externalLinks) &&
                      renderLinkList({
                        variant: "compact",
                        originType: EntityOriginType.External,
                        links: externalLinks,
                      })}
                  </Stack>
                )}
              </Stack>
            </Box>
          </Box>
        </AppPopoverContent>
      </AppPopover>
    </Box>
  );
}
